import HomeLayoutControls from "@components/HomePage/HomeLayoutControls";
import { leaveEditModeGuard } from "@components/HomePage/leaveEditModeGuard";
import { GlassAvatarToggleButton } from "@components/UserAvatar";
import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import useDesktopEnterpriseSession from "@ext/enterprise/components/SingInOut/hooks/useDesktopEnterpriseSession";
import { relocateToUrl } from "@ext/enterprise/components/SingInOut/hooks/useSignIn";
import useSignOut from "@ext/enterprise/components/SingInOut/hooks/useSignOut";
import { useEnterpriseSignIn } from "@ext/enterprise/components/SingInOut/SignInEnterprise";
import { GesCloudApi } from "@ext/enterprise-cloud/GesCloudApi";
import t from "@ext/localization/locale/translate";
import { Level } from "@ext/settings/logic/settings";
import { WorkspaceToolsAccountItem } from "@ext/workspaceTools/components"; // workspace-tools seam
import {
	Avatar,
	AvatarFallback,
	AvatarLabel,
	AvatarLabelAvatar,
	AvatarLabelDescription,
	AvatarLabelTitle,
	getAvatarFallback,
} from "@ui-kit/Avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	DropdownMenuTriggerButton,
} from "@ui-kit/Dropdown";
import { GlassToolbarToggleButton } from "@ui-kit/GlassToolbar";
import { Icon } from "@ui-kit/Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useState } from "react";
import { openAppSettings } from "./AppSettingsTrigger";
import { ThemeMenuItem } from "./ThemeMenuItem";

interface UserMenuProps {
	showHomeLayoutControls?: boolean;
	showThemeToggle?: boolean;
	triggerVariant?: "default" | "glass";
}

const UserMenu = ({
	showHomeLayoutControls = false,
	showThemeToggle = false,
	triggerVariant = "default",
}: UserMenuProps) => {
	const { isWeb, isTauri, isNext } = usePlatform();
	const router = useRouter();
	const apiUrlCreator = ApiUrlCreatorService.value;
	const pageDataContext = PageDataContextService.value;
	const { isCurrentEnterpriseSession, shouldOpenTauriGesModal } = useDesktopEnterpriseSession();
	const { onLogoutClick } = useSignOut();
	const userInfo = pageDataContext?.user?.info;
	const gesUrl = pageDataContext?.conf?.activeGesUrl;
	const isLogged = isCurrentEnterpriseSession;
	const [dropdownOpen, setDropdownOpen] = useState(false);
	// Sign-in entry is shown only where it actually applies (enterprise endpoint
	// available, or a Tauri GES sign-in modal). Plain OSS/static have none.
	const canSignIn = !!gesUrl || (isTauri && shouldOpenTauriGesModal);
	const hasAuthAction = isLogged || canSignIn;

	const enterpriseSignIn = useEnterpriseSignIn({ gesUrl, isWeb, isTauri, apiUrlCreator, router });

	const handleSignIn = async () => {
		if (isTauri && shouldOpenTauriGesModal) {
			const gesCloudUrl = await GesCloudApi.getCloudInstanceUrl();

			ModalToOpenService.setValue(ModalToOpen.TauriGesSignIn, { gesCloudUrl });
			return;
		}
		if (!gesUrl) return;
		await enterpriseSignIn();
	};

	// The portal has no editor workspace to leave: sign-out goes through its own `/api/auth/logout`.
	const handleSignOut = isNext
		? () => relocateToUrl(apiUrlCreator.getAuthUrl(router, true).toString())
		: onLogoutClick;

	const openSettings = () => openAppSettings(Level.app);
	const onOpenChange = async (open: boolean) => {
		if (open && !(await leaveEditModeGuard())) return;
		setDropdownOpen(open);
	};

	const fallback = getCode(userInfo?.name, userInfo?.mail);
	const avatar = isLogged ? (
		<Avatar size={triggerVariant === "glass" ? "lg" : "sm"}>
			<AvatarFallback uniqueId={userInfo?.mail ?? ""}>{fallback}</AvatarFallback>
		</Avatar>
	) : (
		<Icon className={triggerVariant === "default" ? "h-5 w-5 shrink-0 stroke-[1.6]" : ""} icon="user-round" />
	);

	return (
		<DropdownMenu onOpenChange={onOpenChange} open={dropdownOpen}>
			<Tooltip>
				<TooltipContent>
					<p>{isLogged ? (userInfo?.name ?? t("account")) : t("account")}</p>
				</TooltipContent>
				<TooltipTrigger asChild>
					{triggerVariant === "glass" ? (
						<DropdownMenuTrigger asChild>
							{isLogged ? (
								<GlassAvatarToggleButton active={dropdownOpen} aria-label={t("account")}>
									{avatar}
								</GlassAvatarToggleButton>
							) : (
								<GlassToolbarToggleButton active={dropdownOpen} aria-label={t("account")} focusable>
									{avatar}
								</GlassToolbarToggleButton>
							)}
						</DropdownMenuTrigger>
					) : (
						<DropdownMenuTriggerButton
							aria-label={t("account")}
							className="aspect-square p-0"
							variant="ghost"
						>
							{avatar}
						</DropdownMenuTriggerButton>
					)}
				</TooltipTrigger>
			</Tooltip>
			<DropdownMenuContent align="end" className="min-w-[240px] rounded-xl">
				<DropdownMenuGroup>
					{isLogged ? (
						<>
							<DropdownMenuItem className="pointer-events-none">
								<AvatarLabel size="md">
									<AvatarLabelAvatar>
										<AvatarFallback>{fallback}</AvatarFallback>
									</AvatarLabelAvatar>
									<AvatarLabelTitle>{userInfo?.name ?? ""}</AvatarLabelTitle>
									<AvatarLabelDescription>{userInfo?.mail ?? ""}</AvatarLabelDescription>
								</AvatarLabel>
							</DropdownMenuItem>
						</>
					) : (
						<>
							<DropdownMenuLabel className="text-sm font-semibold">{t("account")}</DropdownMenuLabel>
						</>
					)}
					<DropdownMenuSeparator />

					{showHomeLayoutControls && <HomeLayoutControls />}
					{showThemeToggle && <ThemeMenuItem />}

					{!isNext && (
						<DropdownMenuItem className="rounded-lg" onSelect={openSettings}>
							<Icon icon="settings" />
							{t("app-settings.title")}
						</DropdownMenuItem>
					)}
					{/* workspace-tools seam */}
					<WorkspaceToolsAccountItem />
					{hasAuthAction && <DropdownMenuSeparator />}
					{!isLogged && canSignIn && (
						<DropdownMenuItem onSelect={handleSignIn}>
							<Icon icon="log-in" />
							{t("sing-in")}
						</DropdownMenuItem>
					)}
					{isLogged && (
						<DropdownMenuItem className="rounded-lg" onSelect={handleSignOut}>
							<Icon icon="log-out" />
							{t("sing-out")}
						</DropdownMenuItem>
					)}
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

const getCode = (name?: string, mail?: string): string => {
	let code = getAvatarFallback(name ?? "");
	if (!code) code = mail?.[0] ?? "";
	return (code ?? "").toUpperCase();
};

export default UserMenu;
